import { useMemo, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import useSelectedAiSuggestions from "src/hooks/ai/useSelectedAiSuggestions";
import Table from "src/components/Unifize/Table";
import TableCell from "./TableCell";

import { batchSetChecklistValues } from "src/actions/checklist";
import { AiSuggestionState } from "src/constants/ai";
import { TableContainer } from "./styles";
import { generateSelectedFieldsMap } from "src/utils/ai";

import type { RoomId, MessageID, FieldId } from "src/types";
import type { AiNotificationData, AiChecklistSuggestion } from "src/ts-types";
import AddToChecklist from "./AddToChecklist";

type Props = {
  data: AiNotificationData;
  messageId: MessageID;
  roomId: RoomId;
  isDisabled: boolean;
};

const SuggestionTable = ({ data, messageId, roomId, isDisabled }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { jobId, state, fields, sourceField } = data;
  const { otherSettings = {} } = sourceField;
  const { autoFill = false } = otherSettings;

  useEffect(() => {
    if (
      [
        AiSuggestionState.Filled,
        AiSuggestionState.Error,
        AiSuggestionState.Canceled,
      ].includes(state)
    ) {
      setIsLoading(false);
    }
  }, [state]);

  /*
  // Backend doesn't send filled AI sugguestions, so we're not using it yet
  const initialSelections = useMemo(() => {
    const selections: AiSuggestionSelectionMap = new Map();

    filled.forEach(fieldId => {
      selections.set(fieldId, [new Set()]);
    });

    return selections;
  }, [filled, rejected]);*/

  const {
    checklistFieldSuggestionsMap,
    selectedSuggestions,
    toggleSuggestion,
    isSuggestionSelected,
  } = useSelectedAiSuggestions(fields);
  const selectedFields: Set<FieldId> = useMemo(
    () =>
      new Set(
        Array.from(selectedSuggestions.keys()).filter((key) => {
          const suggestions = selectedSuggestions.get(key);
          return (
            suggestions &&
            suggestions.length > 0 &&
            suggestions.some((suggestion) => suggestion != null)
          );
        }),
      ),
    [selectedSuggestions],
  );

  const handleChecklistFill = useCallback(() => {
    if (selectedFields.size === 0) return;
    const selectedFieldsMap = generateSelectedFieldsMap(
      selectedFields,
      selectedSuggestions,
      checklistFieldSuggestionsMap,
    );
    setIsLoading(true);
    dispatch(
      batchSetChecklistValues({
        fieldId: sourceField.id,
        roomId,
        fields,
        selected: selectedFieldsMap,
        messageId,
        jobId,
      }),
    );
  }, [
    selectedFields,
    generateSelectedFieldsMap,
    sourceField,
    selectedSuggestions,
    checklistFieldSuggestionsMap,
  ]);

  const cellRenderer = (row: AiChecklistSuggestion) => {
    const { id, type } = row;

    return (
      <TableCell
        fieldId={id}
        fieldType={type}
        suggestion={row}
        selectionSuggestions={selectedSuggestions.get(id) ?? []}
        isSuggestionSelected={isSuggestionSelected}
        toggleSuggestion={toggleSuggestion}
        isDisabled={isDisabled}
      />
    );
  };

  const columns = [
    {
      label: "Field label",
      accessor: "name",
    },
    {
      label: "Suggested Value",
      accessor: "value",
      renderCell: cellRenderer,
    },
  ];

  useEffect(() => {
    if (
      [
        AiSuggestionState.Filled,
        AiSuggestionState.Error,
        AiSuggestionState.Canceled,
      ].includes(state)
    ) {
      setIsLoading(false);
    }
  }, [state]);

  return (
    <TableContainer>
      <Table
        selectable={!isDisabled && !autoFill}
        columns={columns}
        isDisabled={isDisabled}
        data={fields}
        selectedRows={selectedFields}
        onRowSelect={toggleSuggestion}
      />
      {!autoFill && state !== AiSuggestionState.Filled && (
        <AddToChecklist
          isLoading={isLoading}
          selectedFields={selectedFields}
          handleChecklistFill={handleChecklistFill}
        />
      )}
    </TableContainer>
  );
};

export default SuggestionTable;
